import { IActivity, INewUser } from "@wingspanhq/users/dist/lib/interfaces";
import {
  ExternalBankAccountList,
  PayerSettings
} from "@wingspanhq/wingspan-api-sdk";
import { WSQueryResult } from "@ws-react-query";
import { WSServiceError } from "../../../utils/serviceHelper";
import { selectorExternalBankAccountFullName } from "@selectors/selectorExternalBankAccountFullName";
import { PayrollFundingMethod } from "../steps/PayrollFundingMethod";
import {
  OnboardingContext,
  OnboardingModulePayrollFundingMethod
} from "../types";
import { isUserIdsIncluded } from "../../../userFeatures/utils/isUserIdsIncluded";
import { PAYROLL_FUNDING_METHOD_MANAGED_BY_ORGANIZATION } from "../../../userFeatures/helpers";
import {
  buildOnboardingStep,
  UseCreateOnboardingStep,
  useOnboardingStepQueryConfig
} from "../components/OnboardingFlowUtils";
import { useUserId } from "../../../query/hooks/helpers";
import {
  useQueryActivities,
  useQueryUserProfile
} from "../../../query/users/queries";
import { queriesExternalBankAccounts, queriesPayers } from "@query";
import { FlowStep } from "../../../shared/components/FlowSwitch/types";

const getStatus = (
  payerSettings?: PayerSettings,
  activity?: IActivity,
  isManaged?: boolean
) => {
  const isActivityComplete =
    !!activity?.events?.onboardingFundingMethodCompletedAt;

  const hasFundingSource =
    !!payerSettings?.payrollSettings?.defaultFundingSource;

  if (isManaged) {
    if (!isActivityComplete) {
      return "None";
    }

    if (!hasFundingSource) {
      return "Pending";
    }
  } else {
    if (!hasFundingSource) {
      return "None";
    }
  }

  return "Complete";
};

export const getPayrollFundingMethodStepConfig = (
  _: OnboardingModulePayrollFundingMethod,
  __: OnboardingContext,
  payerSettings?: PayerSettings,
  externalBankAccounts?: ExternalBankAccountList,
  activity?: IActivity,
  user?: INewUser
): FlowStep => {
  const slug = "payroll-funding-method";

  const isPayrollFundingMethodManagedByOrganization = isUserIdsIncluded(
    user,
    PAYROLL_FUNDING_METHOD_MANAGED_BY_ORGANIZATION
  );

  const status = getStatus(
    payerSettings,
    activity,
    isPayrollFundingMethodManagedByOrganization
  );

  const externalBankAccount = externalBankAccounts?.data?.find(
    externalBankAccount =>
      externalBankAccount.id ===
      payerSettings?.payrollSettings?.defaultFundingSource?.id
  );

  const accountText = externalBankAccount
    ? selectorExternalBankAccountFullName(externalBankAccount) + " "
    : "";

  return {
    title: "Payroll funding method",
    slug,
    component: PayrollFundingMethod,

    status,
    result: {
      Complete: {
        title: "Your payroll funding method was added",
        description: `The payroll funding method ${accountText}was added. Click continue for the next step.`
      },
      Pending: {
        type: "card",
        title: "Awaiting payroll funding method",
        description:
          "A payroll funding method must to added to send scheduled payments.  Any incomplete setup tasks will appear on your “Get started” homepage. Incomplete tasks will delay your ability to send payments. Click continue for the next step."
      }
    }
  };
};

export const usePayrollFundingMethodStep: UseCreateOnboardingStep<
  OnboardingModulePayrollFundingMethod
> = modules => {
  const config = useOnboardingStepQueryConfig(
    modules,
    "payroll_funding_method"
  );
  const userId = useUserId();
  const queryPayerSettings = queriesPayers.getCurrentPayerSettings.useQuery(
    undefined,
    config
  );
  const queryExternalBankAccounts =
    queriesExternalBankAccounts.listExternalBankAccounts.useQuery(
      undefined,
      config
    );
  const queryActivity = useQueryActivities(userId, config);
  const queryUser = useQueryUserProfile(userId, config);

  return (onboardingModule, contextValue) =>
    buildOnboardingStep({
      getConfig: getPayrollFundingMethodStepConfig,
      module: onboardingModule,
      context: contextValue,
      queries: [
        queryPayerSettings as unknown as WSQueryResult<
          PayerSettings,
          WSServiceError
        >,
        queryExternalBankAccounts as unknown as WSQueryResult<
          ExternalBankAccountList,
          WSServiceError
        >,
        queryActivity,
        queryUser
      ]
    });
};
